// CORRECTING A RECORDED CHARGE — its amount, its date, who issued it.
//
// The real invoice lives in a partner's own accounting software; this corrects what was
// RECORDED about it. It calls the invoice's existing update (PUT /invoices/:id), and the
// server keeps every guard it always had: the amount may not drop below what has been
// collected, the due date is re-derived from the new date by the client's own terms, the
// status is recomputed from the payments, and a cancelled charge cannot be edited.
//
// WHAT EACH FIELD MOVES:
//   amount  revenue, so profit moves by the difference. The gap does not — it is
//           cash-based and reads only what was collected.
//   date    the due date, and so whether the charge is late. No figure.
//   issuer  the partner the next payment is prefilled with, and nothing else. Since
//           migration 068 the gap credits each PAYMENT to its own collector, so
//           correcting the issuer moves no money that was already collected.
//
// A RETAINER MONTH KEEPS ITS MONTH. The server re-derives the period from the date when
// none is sent, so correcting the date of an invoice issued on 03/08 for July would
// quietly move it to August. The row's own month is always sent back unchanged.
import { useState } from 'react';
import useAsync from '../hooks/useAsync';
import { issuerOptions } from '../utils/issuer';
import { invoicesApi, authApi } from '../api';
import he from '../i18n/he';
import Modal from './Modal';
import { NumberInput, DateInput, Select } from './Form';
import { ErrorText } from './Loader';
import { formatMoney } from './Money';
import ChargeHeader from './ChargeHeader';

const t = he.collections.board;

export default function CorrectChargeModal({ card, row, onClose, onSaved }) {
  const { data: partners } = useAsync(() => authApi.partners());
  const [form, setForm] = useState({
    amount_net: String(row.amount),
    issue_date: row.issueDate || '',
    issuing_partner_id: row.issuingPartnerId || '',
  });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const set = (key) => (e) => setForm({ ...form, [key]: e.target.value });
  // A charge the system wrote while its default was undecided (074) may stay undecided
  // through a correction; choosing a partner here decides it for this charge alone.
  const partnerOptions = row.issuingPartnerId
    ? (partners || []).map((p) => ({ value: p.id, label: p.name }))
    : issuerOptions(partners);
  const collected = Number(row.paid) > 0;

  const submit = async () => {
    if (saving) return;
    setSaving(true);
    setError(null);
    try {
      await invoicesApi.update(row.invoiceId, {
        amount_net: form.amount_net,
        issue_date: form.issue_date,
        issuing_partner_id: Number(form.issuing_partner_id) || null,
        // Sent back unchanged — see the header note. A job charge has none.
        ...(row.periodMonth ? { period_month: row.periodMonth } : {}),
      });
      onSaved();
    } catch (e) {
      setError(e);
      setSaving(false);
    }
  };

  return (
    <Modal
      title={t.correctTitle}
      onClose={onClose}
      onSave={submit}
      saveLabel={t.correctConfirm}
      saveDisabled={saving}
    >
      <ChargeHeader card={card} row={row} />
      <NumberInput label={t.amount} value={form.amount_net} onChange={set('amount_net')} />
      {collected && <p className="field-note">{t.correctAmountFloor(formatMoney(row.paid))}</p>}
      <DateInput label={t.invoiceDate} value={form.issue_date} onChange={set('issue_date')} />
      <Select
        label={t.issuedBy}
        value={form.issuing_partner_id}
        onChange={set('issuing_partner_id')}
        options={partnerOptions}
      />

      {error && <ErrorText error={error} />}
    </Modal>
  );
}
